import {afterNextRender, ChangeDetectorRef, Component, ElementRef, EventEmitter, Inject, Input, NgZone, OnChanges, OnDestroy, Output, PLATFORM_ID, SimpleChanges, ViewChild} from '@angular/core';
import {isPlatformBrowser} from '@angular/common';
import type * as maplibregl from 'maplibre-gl';
import {MarkerInterface} from './marker.interface';
import {MAP_PROVIDER_CONFIG} from './map-provider.config';
import {MapStyleController} from './map-style-controller';
import {AppearanceService} from '../../core/services/appearance.service';
import {ResponseNotificationService} from '../../core/services/response-notification.service';
import {TranslateService} from '@ngx-translate/core';
import {distinctUntilChanged, map, Subscription} from 'rxjs';

@Component({
  selector: 'app-map',
  host: {'data-pull-to-refresh-ignore': ''},
  templateUrl: './map.component.html',
  styleUrls: ['./map.component.scss']
})
export class MapComponent implements OnChanges, OnDestroy {
  @Input() lat: number = 0;
  @Input() lon: number = 0;
  @Input() zoom: number = 1;
  @Input() draggable: boolean = false;
  @Input() showNavigationControls = false;
  @Input() height: string = '320px';
  @Input() markers: MarkerInterface[] = [];
  @Input() selectedMarkerId?: string;
  @Input() selectedMarkerZoom: number = 13;
  @Input() selectedMarkerFocusVersion = 0;
  /** Height of the bottom portion covered by an overlay, in map pixels. */
  @Input() cameraBottomInset = 0;
  @Input() showSelectedMarkerPopup = false;
  @Input() selectedMarkerDetailsLabel?: string;
  @Input() contentReady = true;
  @Input() active = true;
  @Input() allowScrollWheelZoom: boolean = false;
  @Input() clusterMarkers: boolean = false;
  @Input() fitToMarkers: boolean = false;
  @Output() placeSelected = new EventEmitter<any>();
  @Output() public markerSelected = new EventEmitter<MarkerInterface>();
  @Output() public markerDetailsRequested = new EventEmitter<MarkerInterface>();
  @Output() public markerPopupClosed = new EventEmitter<void>();
  @Output() public mapClicked = new EventEmitter<MarkerInterface>();
  @Output() public mapReady = new EventEmitter<void>();
  @Output() public isRenderedEmitter = new EventEmitter<void>();
  @ViewChild('map', {static: true}) mapContainer: ElementRef<HTMLDivElement>;

  public layerMarkers: maplibregl.Marker[] = [];
  public layerCircles: string[] = [];
  public circleRadius?: string;
  public isRendered = false;
  /** The MapLibre style is ready for interaction and source/layer mutations. */
  public isLoaded = false;
  public loadFailed = false;
  public readonly MAP_PROVIDER_CONFIG = MAP_PROVIDER_CONFIG;
  private map?: maplibregl.Map;
  private mapModule?: typeof maplibregl;
  private loadTimeout?: ReturnType<typeof setTimeout>;
  private mapLoaded = false;
  private circleCenter?: MarkerInterface;
  private pendingCircle?: {marker: MarkerInterface; radius: number};
  private resizeObserver?: ResizeObserver;
  private loadingMap = false;
  private readinessVersion = 0;
  private idleListener?: () => void;
  private appliedCenter?: string;
  private appliedMarkers?: string;
  private appliedSelectedMarkerId?: string;
  private appliedSelectedMarkerPosition?: string;
  private appliedSelectedMarkerFocusVersion = 0;
  private appliedCameraBottomInset = 0;
  private appliedShowSelectedMarkerPopup?: boolean;
  private appliedContentReady?: boolean;
  private appliedClusterMarkers?: boolean;
  private readonly clusterSourceId = 'app-map-markers';
  private readonly clusterLayerIds = ['app-map-marker-clusters', 'app-map-marker-count', 'app-map-marker-points', 'app-map-marker-selected'];
  private readonly markerFallbackSourceId = 'app-map-marker-points-fallback';
  private readonly markerFallbackLayerId = 'app-map-marker-points-fallback';
  private clusterClickHandlers?: {cluster: (event: any) => void; point: (event: any) => void};
  private selectedMarkerPopup?: maplibregl.Popup;
  private styleController?: MapStyleController;
  private appearanceSubscription?: Subscription;
  private pendingCircleRemoval = false;
  private pendingReadiness = false;
  private destroyed = false;

  constructor(
    @Inject(PLATFORM_ID) private readonly platformId: object,
    private readonly zone: NgZone,
    private readonly changeDetectorRef: ChangeDetectorRef,
    private readonly appearance: AppearanceService,
    private readonly notifications: ResponseNotificationService,
    private readonly translate: TranslateService,
  ) {
    afterNextRender(() => {
      if (!this.destroyed && isPlatformBrowser(this.platformId)) {
        this.appearanceSubscription = this.appearance.settings$.pipe(
          map(settings => settings.layoutMode), distinctUntilChanged(),
        ).subscribe(mode => void this.styleController?.setMode(mode));
        this.observeContainerSize();
        this.activateIfReady();
      }
    });
  }

  public ngOnDestroy(): void {
    this.destroyed = true;
    this.appearanceSubscription?.unsubscribe();
    this.styleController?.destroy();
    this.clearLoadTimeout();
    this.cancelReadinessCycle();
    this.resizeObserver?.disconnect();
    this.removeSelectedMarkerPopup();
    this.map?.remove();
  }

  public ngOnChanges(changes: SimpleChanges): void {
    if (changes.active && this.active) {
      this.activateIfReady();
    }
    if (!this.map || !this.mapLoaded || this.styleController?.loading) {
      return;
    }
    const center = this.getCenterSignature();
    const markers = this.getMarkersSignature();
    const centerChanged = center !== this.appliedCenter;
    const markersChanged = markers !== this.appliedMarkers;
    const selectionChanged = this.selectedMarkerId !== this.appliedSelectedMarkerId;
    const selectedMarkerPosition = this.getSelectedMarkerPosition();
    const selectedMarkerMoved = !!selectedMarkerPosition && selectedMarkerPosition !== this.appliedSelectedMarkerPosition;
    const focusRequested = this.selectedMarkerFocusVersion !== this.appliedSelectedMarkerFocusVersion;
    const insetChanged = this.cameraBottomInset !== this.appliedCameraBottomInset;
    const popupModeChanged = this.showSelectedMarkerPopup !== this.appliedShowSelectedMarkerPopup;
    const contentReadyChanged = this.contentReady !== this.appliedContentReady;
    const clusterModeChanged = this.clusterMarkers !== this.appliedClusterMarkers;

    if (centerChanged && this.hasValidCenter()) {
      this.map.jumpTo({center: [this.lon, this.lat], zoom: this.zoom});
      this.appliedCenter = center;
    }
    if (markersChanged || clusterModeChanged) {
      this.renderMarkers();
      this.appliedMarkers = markers;
      this.appliedClusterMarkers = this.clusterMarkers;
    }
    if (selectionChanged || popupModeChanged) {
      this.setSelectedMarker(this.selectedMarkerId);
      this.appliedSelectedMarkerId = this.selectedMarkerId;
      this.appliedShowSelectedMarkerPopup = this.showSelectedMarkerPopup;
    }
    if (selectionChanged || selectedMarkerMoved || focusRequested || insetChanged || (centerChanged && !!this.selectedMarkerId)) {
      this.focusSelectedMarker();
    }
    this.appliedSelectedMarkerPosition = selectedMarkerPosition;
    this.appliedSelectedMarkerFocusVersion = this.selectedMarkerFocusVersion;
    this.appliedCameraBottomInset = this.cameraBottomInset;
    this.appliedContentReady = this.contentReady;
    if (this.contentReady && (centerChanged || markersChanged || clusterModeChanged || contentReadyChanged)) {
      this.beginReadinessCycle();
    }
  }

  public async initMap(): Promise<void> {
    if (this.map) {
      this.map.resize();
      return;
    }
    await this.loadMap();
  }

  public invalidateSize(): void {
    this.map?.resize();
  }

  public updateMapCenter(marker: MarkerInterface): void {
    if (this.isValidCoordinates(marker?.lon, marker?.lat)) {
      this.map?.setCenter([marker.lon, marker.lat]);
    }
  }

  /**
   * Starts a new reveal cycle after imperative marker/circle updates. The map
   * stays covered until MapLibre has rendered the current view.
   */
  public waitUntilReady(): void {
    if (this.map) {
      this.map.jumpTo({center: this.map.getCenter(), zoom: this.map.getZoom()});
    }
    this.beginReadinessCycle();
  }

  /** Centers the map on a marker and only zooms in when the current view is too broad. */
  public focusMarker(marker: MarkerInterface, minimumZoom: number = 11): void {
    if (!this.map || !this.mapLoaded || !this.isValidCoordinates(marker?.lon, marker?.lat)) {
      return;
    }

    this.map.easeTo({
      center: [marker.lon, marker.lat],
      zoom: Math.max(this.map.getZoom(), minimumZoom),
      offset: [0, this.cameraBottomInset ? -this.cameraBottomInset / 2 : 0],
      duration: 350
    });
  }

  public addMarker(marker: MarkerInterface): void {
    if (!this.map || !this.mapModule || !this.isValidCoordinates(marker?.lon, marker?.lat)) {
      return;
    }

    const element = document.createElement('img');
    element.className = marker.selected ? 'map-marker map-marker--selected' : 'map-marker';
    element.dataset.markerId = marker.id || '';
    element.src = marker.icon || 'assets/icons/car/car-meeting.png';
    element.alt = marker.title || '';
    element.width = 32;
    element.height = 32;

    const mapMarker = new this.mapModule.Marker({element, anchor: 'bottom'})
      .setLngLat([marker.lon, marker.lat])
      .addTo(this.map);

    this.layerMarkers.push(mapMarker);
    element.addEventListener('click', () => this.zone.run(() => this.markerSelected.emit(marker)));
  }

  public retry(): void {
    this.styleController?.destroy();
    this.styleController = undefined;
    this.clearLoadTimeout();
    this.cancelReadinessCycle();
    this.map?.remove();
    this.map = undefined;
    this.mapModule = undefined;
    this.removeSelectedMarkerPopup();
    this.layerMarkers = [];
    this.layerCircles = [];
    this.circleRadius = undefined;
    this.circleCenter = undefined;
    this.mapLoaded = false;
    this.isLoaded = false;
    this.appliedCenter = undefined;
    this.appliedMarkers = undefined;
    this.appliedSelectedMarkerId = undefined;
    this.appliedSelectedMarkerPosition = undefined;
    this.appliedShowSelectedMarkerPopup = undefined;
    this.appliedContentReady = undefined;
    this.appliedClusterMarkers = undefined;
    this.isRendered = false;
    this.loadFailed = false;
    this.activateIfReady();
  }

  public addCircleRadius(marker: MarkerInterface, radius: number): void {
    if (!this.isValidCoordinates(marker?.lon, marker?.lat) || radius <= 0) {
      this.pendingCircle = undefined;
      this.removeCircle();
      return;
    }

    // Tile/GeoJSON refreshes also make isStyleLoaded() false, but do not prevent
    // layer mutations. Only an actual style replacement needs to defer them.
    if (!this.map || !this.mapLoaded || this.styleController?.loading) {
      this.pendingCircle = {marker, radius};
      this.pendingCircleRemoval = false;
      return;
    }

    this.removeCircle();
    this.circleRadius = 'search-radius';
    this.circleCenter = {lat: marker.lat, lon: marker.lon};
    this.layerCircles = [this.circleRadius];
    this.map.addSource(this.circleRadius, {type: 'geojson', data: this.circleFeature(marker, radius)});
    this.map.addLayer({id: `${this.circleRadius}-fill`, type: 'fill', source: this.circleRadius, paint: {'fill-color': '#4285f4', 'fill-opacity': 0.3}});
    this.map.addLayer({id: `${this.circleRadius}-line`, type: 'line', source: this.circleRadius, paint: {'line-color': '#4285f4', 'line-width': 2}});
  }

  public updateCircleRadius(radius: number): void {
    if (radius <= 0) {
      this.removeCircle();
      return;
    }
    if (!this.map || !this.mapLoaded || this.styleController?.loading) {
      if (this.pendingCircle) {
        this.pendingCircle = {...this.pendingCircle, radius};
      } else if (this.circleCenter) {
        this.pendingCircle = {marker: this.circleCenter, radius};
        this.pendingCircleRemoval = false;
      }
      return;
    }
    if (!this.circleRadius) {
      const marker = this.layerMarkers[this.layerMarkers.length - 1]?.getLngLat();
      if (marker) {
        this.addCircleRadius({lat: marker.lat, lon: marker.lng}, radius);
      }
      return;
    }
    const source = this.map.getSource(this.circleRadius) as maplibregl.GeoJSONSource | undefined;
    if (this.circleCenter) {
      source?.setData(this.circleFeature(this.circleCenter, radius));
    }
  }

  public removeMarker(marker: maplibregl.Marker): void {
    marker.remove();
    this.layerMarkers = this.layerMarkers.filter((item) => item !== marker);
  }

  public removeCircle(_circle?: string): void {
    this.pendingCircle = undefined;
    if (this.styleController?.loading) {
      this.pendingCircleRemoval = true;
      return;
    }
    this.pendingCircleRemoval = false;
    if (!this.map || !this.circleRadius) {
      return;
    }
    [`${this.circleRadius}-line`, `${this.circleRadius}-fill`].forEach((id) => {
      if (this.map?.getLayer(id)) {
        this.map.removeLayer(id);
      }
    });
    if (this.map.getSource(this.circleRadius)) {
      this.map.removeSource(this.circleRadius);
    }
    this.layerCircles = [];
    this.circleRadius = undefined;
    this.circleCenter = undefined;
  }

  private getCenterSignature(): string {
    return `${this.lat}|${this.lon}|${this.zoom}`;
  }

  private getMarkersSignature(): string {
    return this.markers
      .map((marker) => `${marker.id || ''}|${marker.lat}|${marker.lon}|${marker.icon || ''}|${marker.title || ''}|${marker.content || ''}`)
      .join('||');
  }

  private setSelectedMarker(markerId?: string): void {
    this.layerMarkers.forEach((marker) => {
      marker.getElement().classList.toggle('map-marker--selected', marker.getElement().dataset.markerId === markerId);
    });
    this.setClusterSelectedMarker(markerId);
    this.updateSelectedMarkerPopup(markerId);
  }

  private setClusterSelectedMarker(markerId?: string): void {
    if (!this.map?.getLayer('app-map-marker-selected')) {
      return;
    }
    this.map.setFilter('app-map-marker-selected', [
      'all',
      ['!', ['has', 'point_count']],
      ['==', 'markerId', markerId || ''],
    ] as any);
  }

  private updateSelectedMarkerPopup(markerId?: string): void {
    this.removeSelectedMarkerPopup();
    if (!this.showSelectedMarkerPopup || !this.map || !this.mapModule) {
      return;
    }

    const marker = this.markers.find((item) => item.id === markerId);
    if (!marker || !this.isValidCoordinates(marker.lon, marker.lat)) {
      return;
    }

    const popup = new this.mapModule.Popup({closeButton: true, closeOnClick: false, offset: 14})
      .setLngLat([marker.lon, marker.lat])
      .setDOMContent(this.createSelectedMarkerPopupContent(marker))
      .addTo(this.map);
    this.selectedMarkerPopup = popup;
    popup.on('close', () => {
      if (this.selectedMarkerPopup !== popup) {
        return;
      }
      this.selectedMarkerPopup = undefined;
      this.zone.run(() => this.markerPopupClosed.emit());
    });
  }

  private removeSelectedMarkerPopup(): void {
    const popup = this.selectedMarkerPopup;
    this.selectedMarkerPopup = undefined;
    popup?.remove();
  }

  private createSelectedMarkerPopupContent(marker: MarkerInterface): HTMLElement {
    const content = document.createElement('div');
    content.className = 'map-selected-marker-popup';

    const title = document.createElement('strong');
    title.textContent = marker.title || '';
    content.appendChild(title);

    if (marker.content) {
      const description = document.createElement('small');
      description.textContent = marker.content;
      content.appendChild(description);
    }

    if (this.selectedMarkerDetailsLabel) {
      const details = document.createElement('button');
      details.type = 'button';
      details.className = 'map-selected-marker-popup__details';
      details.textContent = this.selectedMarkerDetailsLabel;
      details.addEventListener('click', (event) => {
        event.preventDefault();
        event.stopPropagation();
        this.zone.run(() => this.markerDetailsRequested.emit(marker));
      });
      content.appendChild(details);
    }

    return content;
  }

  private getSelectedMarkerPosition(): string | undefined {
    const marker = this.markers.find(item => item.id === this.selectedMarkerId);
    return marker ? `${marker.lon},${marker.lat}` : undefined;
  }

  private focusSelectedMarker(): void {
    const marker = this.markers.find((item) => item.id === this.selectedMarkerId);
    if (marker) {
      this.focusMarker(marker, this.selectedMarkerZoom);
    }
  }

  private renderMarkers(): void {
    if (!this.map || !this.mapLoaded) {
      return;
    }

    [...this.layerMarkers].forEach((marker) => this.removeMarker(marker));
    if (this.clusterMarkers) {
      this.renderClusterMarkers();
    } else {
      this.clearClusterMarkers();
      this.markers.forEach((marker) => this.addMarker(marker));
    }
    this.setSelectedMarker(this.selectedMarkerId);
    if (this.fitToMarkers) {
      this.fitMapToMarkers();
    }
  }

  private renderClusterMarkers(): void {
    if (!this.map) {
      return;
    }
    const data = {
      type: 'FeatureCollection',
      features: this.markers
        .filter((marker) => this.isValidCoordinates(marker?.lon, marker?.lat))
        .map((marker) => ({
          type: 'Feature',
          properties: {markerId: marker.id || ''},
          geometry: {type: 'Point', coordinates: [marker.lon, marker.lat]}
        }))
    };
    const existingSource = this.map.getSource(this.clusterSourceId) as maplibregl.GeoJSONSource | undefined;
    if (existingSource) {
      existingSource.setData(data as any);
      this.updateMarkerFallback(data);
      this.map.triggerRepaint();
      return;
    }

    this.updateMarkerFallback(data);
    this.map.addSource(this.clusterSourceId, {
      type: 'geojson',
      data: data as any,
      cluster: true,
      clusterMaxZoom: 14,
      clusterRadius: 52
    } as any);
    this.map.addLayer({
      id: this.clusterLayerIds[0], type: 'circle', source: this.clusterSourceId,
      filter: ['has', 'point_count'],
      paint: {'circle-color': '#8ee000', 'circle-radius': 18, 'circle-stroke-width': 2, 'circle-stroke-color': '#111'}
    } as any);
    this.map.addLayer({
      id: this.clusterLayerIds[1], type: 'symbol', source: this.clusterSourceId,
      filter: ['has', 'point_count'],
      layout: {'text-field': '{point_count_abbreviated}', 'text-size': 12},
      paint: {'text-color': '#111'}
    } as any);
    this.map.addLayer({
      id: this.clusterLayerIds[2], type: 'circle', source: this.clusterSourceId,
      filter: ['!', ['has', 'point_count']],
      paint: {'circle-color': '#ff3b30', 'circle-radius': 8, 'circle-stroke-width': 2, 'circle-stroke-color': '#fff'}
    } as any);
    this.map.addLayer({
      id: this.clusterLayerIds[3], type: 'circle', source: this.clusterSourceId,
      filter: ['all', ['!', ['has', 'point_count']], ['==', 'markerId', '']],
      paint: {'circle-color': '#4285f4', 'circle-radius': 13, 'circle-stroke-width': 3, 'circle-stroke-color': '#fff'}
    } as any);

    const cluster = (event: any) => this.onClusterClick(event);
    const point = (event: any) => this.onClusterPointClick(event);
    this.clusterClickHandlers = {cluster, point};
    this.map.on('click', this.clusterLayerIds[0], cluster);
    this.map.on('click', this.clusterLayerIds[2], point);
    this.map.on('click', this.clusterLayerIds[3], point);
    this.map.on('click', this.markerFallbackLayerId, point);
    this.map.triggerRepaint();
  }

  private updateMarkerFallback(data: object): void {
    if (!this.map) {
      return;
    }

    const source = this.map.getSource(this.markerFallbackSourceId) as maplibregl.GeoJSONSource | undefined;
    if (source) {
      source.setData(data as any);
      return;
    }

    this.map.addSource(this.markerFallbackSourceId, {type: 'geojson', data: data as any});
    this.map.addLayer({
      id: this.markerFallbackLayerId,
      type: 'circle',
      source: this.markerFallbackSourceId,
      paint: {
        'circle-color': '#ff3b30',
        'circle-opacity': .7,
        'circle-radius': 4,
        'circle-stroke-width': 1,
        'circle-stroke-color': '#fff',
      }
    } as any);
  }

  private clearClusterMarkers(): void {
    if (!this.map) {
      return;
    }
    if (this.clusterClickHandlers) {
      if (this.map.getLayer(this.clusterLayerIds[0])) this.map.off('click', this.clusterLayerIds[0], this.clusterClickHandlers.cluster);
      if (this.map.getLayer(this.clusterLayerIds[2])) this.map.off('click', this.clusterLayerIds[2], this.clusterClickHandlers.point);
      if (this.map.getLayer(this.clusterLayerIds[3])) this.map.off('click', this.clusterLayerIds[3], this.clusterClickHandlers.point);
      if (this.map.getLayer(this.markerFallbackLayerId)) this.map.off('click', this.markerFallbackLayerId, this.clusterClickHandlers.point);
      this.clusterClickHandlers = undefined;
    }
    [...this.clusterLayerIds].reverse().forEach((layerId) => {
      if (this.map?.getLayer(layerId)) {
        this.map.removeLayer(layerId);
      }
    });
    if (this.map.getSource(this.clusterSourceId)) {
      this.map.removeSource(this.clusterSourceId);
    }
    if (this.map.getLayer(this.markerFallbackLayerId)) {
      this.map.removeLayer(this.markerFallbackLayerId);
    }
    if (this.map.getSource(this.markerFallbackSourceId)) {
      this.map.removeSource(this.markerFallbackSourceId);
    }
  }

  private onClusterClick(event: any): void {
    const feature = event?.features?.[0];
    const clusterId = feature?.properties?.cluster_id;
    const coordinates = feature?.geometry?.coordinates as [number, number] | undefined;
    const source = this.map?.getSource(this.clusterSourceId) as any;
    if (!source || clusterId === undefined || !coordinates || coordinates.length !== 2) {
      return;
    }
    source.getClusterExpansionZoom(clusterId, (_error: Error | null, zoom: number) => {
      this.map?.easeTo({center: coordinates, zoom});
    });
  }

  private onClusterPointClick(event: any): void {
    const markerId = event?.features?.[0]?.properties?.markerId;
    const marker = this.markers.find((item) => item.id === markerId);
    if (marker) {
      this.zone.run(() => this.markerSelected.emit(marker));
    }
  }

  private fitMapToMarkers(): void {
    if (!this.map || !this.mapModule) {
      return;
    }
    const validMarkers = this.markers.filter((marker) => this.isValidCoordinates(marker?.lon, marker?.lat));
    if (!validMarkers.length) {
      return;
    }
    if (validMarkers.length === 1) {
      this.map.jumpTo({center: [validMarkers[0].lon, validMarkers[0].lat], zoom: Math.max(this.zoom, 10)});
      return;
    }
    const bounds = new this.mapModule.LngLatBounds();
    validMarkers.forEach((marker) => bounds.extend([marker.lon, marker.lat]));
    this.map.fitBounds(bounds, {padding: 48, maxZoom: 12, duration: 0});
  }

  private observeContainerSize(): void {
    this.resizeObserver = new ResizeObserver(() => {
      if (!this.map) {
        this.activateIfReady();
        return;
      }
      this.map.resize();
      this.map.triggerRepaint();
    });
    this.resizeObserver.observe(this.mapContainer.nativeElement);
  }

  private activateIfReady(): void {
    if (!this.active || !isPlatformBrowser(this.platformId) || !this.hasValidCenter()) {
      return;
    }
    requestAnimationFrame(() => {
      if (!this.destroyed && this.active && this.hasContainerSize() && this.hasValidCenter()) {
        void this.loadMap();
      }
    });
  }

  private hasContainerSize(): boolean {
    const {width, height} = this.mapContainer.nativeElement.getBoundingClientRect();
    return width > 0 && height > 0;
  }

  private hasValidCenter(): boolean {
    return this.isValidCoordinates(this.lon, this.lat);
  }

  private isValidCoordinates(lon: unknown, lat: unknown): boolean {
    const normalizedLon = Number(lon);
    const normalizedLat = Number(lat);
    return Number.isFinite(normalizedLon)
      && Number.isFinite(normalizedLat)
      && normalizedLon >= -180
      && normalizedLon <= 180
      && normalizedLat >= -90
      && normalizedLat <= 90;
  }

  private async loadMap(): Promise<void> {
    if (!isPlatformBrowser(this.platformId) || this.destroyed || this.map || this.loadingMap || !this.active || !this.hasContainerSize() || !this.hasValidCenter()) {
      return;
    }
    this.loadingMap = true;
    this.loadFailed = false;
    this.isRendered = false;
    this.startLoadTimeout();
    let mapModule: typeof maplibregl;
    try {
      mapModule = await import('maplibre-gl');
    } catch {
      this.loadingMap = false;
      this.failLoading();
      return;
    }
    if (this.destroyed) return;
    this.mapModule = mapModule;
    const mode = this.appearance.settings.layoutMode;
    const initialCenter = this.getCenterSignature();
    const map = new mapModule.Map({
      container: this.mapContainer.nativeElement,
      style: MAP_PROVIDER_CONFIG.styleUrls[mode],
      center: [this.lon, this.lat],
      zoom: this.zoom,
      attributionControl: false,
      interactive: this.draggable || this.allowScrollWheelZoom,
      dragPan: this.draggable,
      scrollZoom: this.allowScrollWheelZoom,
      doubleClickZoom: false
    });
    this.map = map;
    if (this.showNavigationControls) {
      map.addControl(new mapModule.NavigationControl({showCompass: false}), 'top-right');
    }
    map.on('load', () => {
      if (this.destroyed || this.map !== map) {
        return;
      }
      this.mapLoaded = true;
      this.isLoaded = true;
      this.loadingMap = false;
      // Inputs may change while the initial style/tiles are still loading.
      if (this.getCenterSignature() !== initialCenter && this.hasValidCenter()) {
        map.jumpTo({center: [this.lon, this.lat], zoom: this.zoom});
      }
      this.renderMarkers();
      this.appliedCenter = this.getCenterSignature();
      this.appliedMarkers = this.getMarkersSignature();
      this.setSelectedMarker(this.selectedMarkerId);
      this.focusSelectedMarker();
      this.appliedSelectedMarkerId = this.selectedMarkerId;
      this.appliedSelectedMarkerPosition = this.getSelectedMarkerPosition();
      this.appliedSelectedMarkerFocusVersion = this.selectedMarkerFocusVersion;
      this.appliedCameraBottomInset = this.cameraBottomInset;
      this.appliedShowSelectedMarkerPopup = this.showSelectedMarkerPopup;
      this.appliedClusterMarkers = this.clusterMarkers;
      this.appliedContentReady = this.contentReady;
      this.map.resize();
      this.applyPendingCircle();
      this.zone.run(() => this.mapReady.emit());
      this.beginReadinessCycle();
      this.styleController = new MapStyleController(
        map, mode,
        [this.clusterSourceId, this.markerFallbackSourceId, 'search-radius'],
        [...this.clusterLayerIds, this.markerFallbackLayerId, 'search-radius-fill', 'search-radius-line'],
        () => {
          this.clearLoadTimeout();
          this.cancelReadinessCycle();
          ++this.readinessVersion;
        },
        () => {
          this.ngOnChanges({});
          if (this.pendingCircleRemoval) this.removeCircle();
          else this.applyPendingCircle();
          if (this.pendingReadiness || !this.isRendered) this.beginReadinessCycle();
        },
        () => this.zone.run(() => this.notifications.emitError(this.translate.instant('shared.map.theme_loading_failed'))),
      );
      void this.styleController.setMode(this.appearance.settings.layoutMode);
    });
    map.on('click', (event) => {
      if (!event?.lngLat) {
        return;
      }
      this.zone.run(() => this.mapClicked.emit({lat: event.lngLat.lat, lon: event.lngLat.lng}));
    });
    map.on('error', () => {
      // Individual tile retries are normal; the controlled 8 s timeout decides
      // whether the map is genuinely unavailable.
    });
  }

  private beginReadinessCycle(): void {
    if (this.styleController?.loading) {
      this.pendingReadiness = true;
      return;
    }
    this.pendingReadiness = false;
    if (!this.map || !this.mapLoaded || !this.contentReady) {
      return;
    }
    this.cancelReadinessCycle();
    this.isRendered = false;
    this.loadFailed = false;
    this.startLoadTimeout();
    const version = ++this.readinessVersion;
    const idleListener = () => {
      if (this.idleListener !== idleListener) {
        return;
      }
      this.idleListener = undefined;
      void this.finishWhenVisible(version);
    };
    this.idleListener = idleListener;
    this.map.once('idle', idleListener);
    this.map.triggerRepaint();
  }

  private applyPendingCircle(): void {
    const pending = this.pendingCircle;
    if (!pending) {
      return;
    }
    this.pendingCircle = undefined;
    this.addCircleRadius(pending.marker, pending.radius);
  }

  private cancelReadinessCycle(): void {
    if (this.map && this.idleListener) {
      this.map.off('idle', this.idleListener);
    }
    this.idleListener = undefined;
  }

  private async finishWhenVisible(version: number): Promise<void> {
    if (!this.map || this.loadFailed || this.isRendered || !this.contentReady) {
      return;
    }
    await this.waitForMarkerImages();
    await this.waitForAnimationFrames(2);
    if (!this.map || this.loadFailed || this.isRendered || version !== this.readinessVersion) {
      return;
    }
    if (!this.map.isStyleLoaded() || (this.map.areTilesLoaded && !this.map.areTilesLoaded())) {
      return;
    }
    this.zone.run(() => {
      this.clearLoadTimeout();
      this.isRendered = true;
      this.isRenderedEmitter.emit();
      this.changeDetectorRef.markForCheck();
    });
    this.invalidateSize();
  }

  private waitForMarkerImages(): Promise<void> {
    const images = Array.from(this.mapContainer.nativeElement.querySelectorAll<HTMLImageElement>('img.map-marker'));
    return Promise.all(images.map((image) => {
      if (image.complete) {
        return Promise.resolve();
      }
      return new Promise<void>((resolve) => {
        image.addEventListener('load', () => resolve(), {once: true});
        image.addEventListener('error', () => resolve(), {once: true});
      });
    })).then(() => undefined);
  }

  private waitForAnimationFrames(frames: number): Promise<void> {
    return new Promise((resolve) => {
      const nextFrame = (remaining: number) => {
        requestAnimationFrame(() => remaining <= 1 ? resolve() : nextFrame(remaining - 1));
      };
      nextFrame(frames);
    });
  }

  private startLoadTimeout(): void {
    this.clearLoadTimeout();
    this.loadTimeout = setTimeout(() => this.failLoading(), 8000);
  }

  private clearLoadTimeout(): void {
    if (this.loadTimeout) {
      clearTimeout(this.loadTimeout);
      this.loadTimeout = undefined;
    }
  }

  private failLoading(): void {
    this.cancelReadinessCycle();
    this.zone.run(() => {
      this.clearLoadTimeout();
      this.loadingMap = false;
      this.loadFailed = true;
      this.isRendered = false;
      this.changeDetectorRef.markForCheck();
    });
  }

  private circleFeature(marker: MarkerInterface, radiusKm: number): {
    type: 'Feature';
    properties: Record<string, never>;
    geometry: {type: 'Polygon'; coordinates: number[][][]};
  } {
    const points = 64;
    const earthRadiusKm = 6371;
    const angularDistance = radiusKm / earthRadiusKm;
    const lat = marker.lat * Math.PI / 180;
    const lon = marker.lon * Math.PI / 180;
    const coordinates: number[][] = [];
    for (let index = 0; index <= points; index++) {
      const bearing = (index * 360 / points) * Math.PI / 180;
      const pointLat = Math.asin(Math.sin(lat) * Math.cos(angularDistance) + Math.cos(lat) * Math.sin(angularDistance) * Math.cos(bearing));
      const pointLon = lon + Math.atan2(Math.sin(bearing) * Math.sin(angularDistance) * Math.cos(lat), Math.cos(angularDistance) - Math.sin(lat) * Math.sin(pointLat));
      coordinates.push([pointLon * 180 / Math.PI, pointLat * 180 / Math.PI]);
    }
    return {type: 'Feature', properties: {}, geometry: {type: 'Polygon', coordinates: [coordinates]}};
  }
}
